import { Box, FormErrorMessage, FormGroup } from '@ksyos/design-system';
import { type ChangeEvent, useState } from 'react';
import type { CardiologyMedication } from '@common/validation/customControls/cardiology/holter/utils/medicationUtils';
import type { HolterExaminationRequestBase } from '@common/validation/customControls/cardiology/holter/utils/utils';
import {
    CalciumAntagonistDihydropyridinesMedicationLabel,
    calciumAntagonistDihydropyridinesMedicationLabels,
    CalciumAntagonistNonDihydropyridinesMedicationLabel,
    calciumAntagonistNonDihydropyridinesMedicationLabels,
    CardiologyMedicationLabel,
    cardiologyMedicationLabels,
    DiureticaMedicationLabel,
    diureticaMedicationLabels,
    initialMedicationData,
} from '@common/validation/customControls/cardiology/holter/utils/medicationUtils';
import { useEditFormSectionInputFieldContext } from '@frontend/formEngine/editForm/useEditFormContext';
import { getValidationErrorTranslation, useProcessActivityEditContext } from '@frontend/utils';
import { MedicationCheckbox } from './MedicationCheckbox';
import { MedicationSubItemCheckboxes } from './MedicationSubItemCheckboxes';

export type CheckboxItem = {
    checkboxLabel: string;
    disabled?: boolean;
    indeterminate?: boolean;
    isChecked: boolean;
    key: string;
    onChangeHandler: (event: ChangeEvent<HTMLInputElement>) => void;
    onChangeText: (event: ChangeEvent<HTMLInputElement>) => void;
    textInputValue: string | null;
    value: boolean | string | null;
};

const validateKeys = ['medication.general', 'medication.diureticaType', 'medication.calciumAntagonistType'];

export function HolterMedicationSelector() {
    const {
        data: { medication },
        errors,
        onChange,
    } = useProcessActivityEditContext<HolterExaminationRequestBase>();
    const {
        field: { label },
    } = useEditFormSectionInputFieldContext();
    const [isChecked, setIsChecked] = useState(medication.general.overige ? !!medication.general.overige : false);
    const [textInputValue, setTextInputValue] = useState<string | null>(medication.general.overige ?? null);

    const medicationItems = getCheckboxItems({
        category: 'general',
        enumType: CardiologyMedicationLabel,
        labels: cardiologyMedicationLabels,
        medicationData: medication,
        onChangeMedication: (e: ChangeEvent<HTMLInputElement>, key: string) => {
            handleCheckboxChangeMedication(e.target.checked, key);
        },
    });

    const diureticaItems = getCheckboxItems({
        category: 'diureticaType',
        enumType: DiureticaMedicationLabel,
        labels: diureticaMedicationLabels,
        medicationData: medication,
        onChangeMedication: (e: ChangeEvent<HTMLInputElement>, key: string) =>
            onChange(
                {
                    medication: {
                        ...medication,
                        diureticaType: { ...medication.diureticaType, [key]: e.target.checked },
                    },
                },
                validateKeys
            ),
    });

    const calciumAntagonistDihydropyridinesItems = getCheckboxItems({
        category: 'calciumAntagonistType',
        enumType: CalciumAntagonistDihydropyridinesMedicationLabel,
        labels: calciumAntagonistDihydropyridinesMedicationLabels,
        medicationData: medication,
        onChangeMedication: (e: ChangeEvent<HTMLInputElement>, key: string) =>
            onChange(
                {
                    medication: {
                        ...medication,
                        calciumAntagonistType: { ...medication.calciumAntagonistType, [key]: e.target.checked },
                    },
                },
                validateKeys
            ),
    });

    const calciumAntagonistNonDihydropyridinesItems = getCheckboxItems({
        category: 'calciumAntagonistType',
        enumType: CalciumAntagonistNonDihydropyridinesMedicationLabel,
        labels: calciumAntagonistNonDihydropyridinesMedicationLabels,
        medicationData: medication,
        onChangeMedication: (e: ChangeEvent<HTMLInputElement>, key: string) =>
            onChange(
                {
                    medication: {
                        ...medication,
                        calciumAntagonistType: { ...medication.calciumAntagonistType, [key]: e.target.checked },
                    },
                },
                validateKeys
            ),
    });

    return (
        <FormGroup
            hasErrors={
                errors.has('medication.general') ||
                errors.has('medication.calciumAntagonistType') ||
                errors.has('medication.diureticaType')
            }
            isRequired={true}
            label={label}
        >
            {medicationItems.map((item) => {
                const { checkboxLabel } = item;

                return (
                    <Box
                        display="flex"
                        flexDirection="column"
                        gap={1}
                        key={`checkbox-${checkboxLabel}`}
                        paddingBottom={2}
                    >
                        <MedicationCheckbox
                            ariaLabel={label}
                            checkBoxItem={item}
                        />
                        {item.key === 'diuretica' && item.value && (
                            <MedicationSubItemCheckboxes
                                ariaLabel={label}
                                checkBoxItems={diureticaItems}
                                errors={errors}
                                name={'diureticaType'}
                            />
                        )}
                        {item.key === 'calciumAntagonist' && item.value && (
                            <>
                                <MedicationSubItemCheckboxes
                                    ariaLabel={label}
                                    checkBoxItems={calciumAntagonistDihydropyridinesItems}
                                    errors={errors}
                                    subTitle={'Dihydropyridines'}
                                />
                                <MedicationSubItemCheckboxes
                                    ariaLabel={label}
                                    checkBoxItems={calciumAntagonistNonDihydropyridinesItems}
                                    errors={errors}
                                    name={'calciumAntagonistType'}
                                    subTitle={'Non-dihydropyridines'}
                                />
                            </>
                        )}
                    </Box>
                );
            })}
            {errors.has('medication.general') && (
                <FormErrorMessage>{getValidationErrorTranslation(errors.get('medication.general'))}</FormErrorMessage>
            )}
        </FormGroup>
    );

    function handleCheckboxChangeMedication(value: boolean | string | null, key: string) {
        let updatedMedication = {
            ...medication,
            general: {
                ...medication.general,
                [key]: value,
            },
        };

        if (key === 'overige' && typeof value === 'boolean') {
            updatedMedication = {
                ...updatedMedication,
                general: {
                    ...medication.general,
                    overige: !value ? null : textInputValue,
                },
            };
            setIsChecked(Boolean(value));
        }

        if (key === 'calciumAntagonist' && !value) {
            updatedMedication = {
                ...updatedMedication,
                calciumAntagonistType: initialMedicationData.calciumAntagonistType,
            };
        }
        if (key === 'diuretica' && !value) {
            updatedMedication = {
                ...updatedMedication,
                diureticaType: initialMedicationData.diureticaType,
            };
        }
        onChange({ medication: updatedMedication }, validateKeys);
    }

    function getCheckboxItems<
        T extends
            | CardiologyMedicationLabel
            | CalciumAntagonistNonDihydropyridinesMedicationLabel
            | CalciumAntagonistDihydropyridinesMedicationLabel
            | DiureticaMedicationLabel,
    >(params: {
        category: keyof CardiologyMedication;
        enumType: {
            [key: string]: string;
        };
        labels: T[];
        medicationData: CardiologyMedication;
        onChangeMedication: (event: ChangeEvent<HTMLInputElement>, key: string) => void;
    }): CheckboxItem[] {
        const { category, enumType, labels, medicationData, onChangeMedication } = params;

        const checkboxLookup = Object.keys(medicationData[category]).map((key) => ({
            checkboxLabel: enumType[key],
            isChecked,
            key: key,
            onChangeHandler: (event: ChangeEvent<HTMLInputElement>) => onChangeMedication(event, key),
            onChangeText: (event: ChangeEvent<HTMLInputElement>) => {
                handleCheckboxChangeMedication(event.target.value, 'overige');
                setTextInputValue(event.target.value ? event.target.value : null);
            },
            textInputValue,
            value: medicationData[category][key as keyof (typeof medicationData)[typeof category]],
        }));

        return labels.map((orderedLabel) =>
            checkboxLookup.find((checkbox) => checkbox.checkboxLabel === orderedLabel)
        ) as CheckboxItem[];
    }
}
